<script lang="ts">
    import {
        getModalStore,
        type ModalStore,
        popup,
        type PopupSettings,
        getToastStore,
        type ToastStore,
    } from "@skeletonlabs/skeleton";
    import { onMount } from "svelte";

    import type { rfp_applications_record, question_data_record } from "$lib/types/supabase";

    import BlackX from "$lib/assets/BlackX.svg";
    import SearchGlass from "$lib/assets/SearchGlass.svg";

    const modalStore: ModalStore = getModalStore();
    const toastStore: ToastStore = getToastStore();

    // Flag to keep track of which menu the user is on
    let mode: "application" | "question" | "manual" = "application";

    let rfp_application_resp: Promise<void>;
    let rfp_application_list: rfp_applications_record[];

    let questions_resp: Promise<void>;
    let questions_list: question_data_record[];
    let question_search_input: string = "";
    let selected_questions: Set<string>;

    let manual_question_name: string = "";
    let manual_question_response: string = "";

    let submitting: boolean = false;

    // Status colors mapping
    const status_colors: Readonly<Record<string, string>> = {
        "Not Started": "bg-purple-500",
        "In Progress": "bg-yellow-500",
        Completed: "bg-green-500",
        Submitted: "bg-blue-500",
        Cancelled: "bg-red-500",
    };

    const manual_submit_popup: PopupSettings = {
        event: "hover",
        target: "manual_submit_popup",
        placement: "top",
    };

    function filter_questions(list: question_data_record[], search: string) {
        const filter_by_search: question_data_record[] = list.filter(
            (ele) =>
                (ele.name ?? "").toLowerCase().includes(search.toLowerCase()) ||
                (ele.response_text ?? "").toLowerCase().includes(search.toLowerCase()),
        );
        return filter_by_search.filter((ele) => ele.name && ele.response_text);
    }

    function format_date(date: string | undefined): string {
        if (!date) return "";
        return new Date(date).toLocaleDateString("en-US", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        });
    }

    // Fetch questions & clear input for when the user chooses an application
    function choose_application(application_id: string | undefined): void {
        mode = "question";
        question_search_input = "";
        selected_questions = new Set();
        questions_resp = fetch(
            "/api/drafts/question?" + new URLSearchParams({ application_id: application_id as string }),
        )
            .then((resp) => resp.json())
            .then((res) => (questions_list = res.results));
    }

    function toggle_question_select(question_id: string | undefined): void {
        if (!question_id) return;

        if (selected_questions.has(question_id)) {
            selected_questions.delete(question_id);
        } else {
            selected_questions.add(question_id);
        }

        selected_questions = selected_questions;
    }

    function submit_manual(): void {
        fetch("/api/qa_pairs", {
            method: "POST",
            body: JSON.stringify({
                type: "manual",
                name: manual_question_name,
                response: manual_question_response,
            }),
        })
            .then((resp) => {
                if (resp.ok) {
                    resp.json().then((res) => {
                        toastStore.trigger({
                            message: "✓ " + res.message,
                            background: "bg-zinc-800 text-green-300 text-center",
                            hideDismiss: true,
                            timeout: 4000,
                        });
                    });
                } else {
                    resp.json()
                        .then((res) => {
                            toastStore.trigger({
                                message: "✗ " + res.message,
                                background: "bg-zinc-800 text-red-400 text-center",
                                hideDismiss: true,
                                timeout: 8000,
                            });
                        })
                        .catch(() => {
                            toastStore.trigger({
                                message:
                                    "✗ Something went wrong. Please try again or contact us if this error persists.",
                                background: "bg-zinc-800 text-red-400 text-center",
                                hideDismiss: true,
                                timeout: 8000,
                            });
                        });
                }
            })

        $modalStore[0].response!({});
        modalStore.clear();
    }

    function submit_questions(): void {
        submitting = true;

        fetch("/api/qa_pairs", {
            method: "POST",
            body: JSON.stringify({
                type: "list",
                question_id_list: Array.from(selected_questions),
            }),
        })
            .then((resp) => {
                if (resp.ok) {
                    resp.json().then((res) => {
                        toastStore.trigger({
                            message: "✓ " + res.message,
                            background: "bg-zinc-800 text-green-300 text-center",
                            hideDismiss: true,
                            timeout: 4000,
                        });
                    });

                    $modalStore[0].response!({});

                    modalStore.clear();
                } else {
                    resp.json()
                        .then((res) => {
                            toastStore.trigger({
                                message: "✗ " + res.message,
                                background: "bg-zinc-800 text-red-400 text-center",
                                hideDismiss: true,
                                timeout: 8000,
                            });
                        })
                        .catch(() => {
                            toastStore.trigger({
                                message:
                                    "✗ Something went wrong. Please try again or contact us if this error persists.",
                                background: "bg-zinc-800 text-red-400 text-center",
                                hideDismiss: true,
                                timeout: 8000,
                            });
                        });
                }
            })
            .finally(() => (submitting = false));
    }

    onMount(() => {
        rfp_application_resp = fetch("/api/drafts/application_metadata")
            .then((resp) => resp.json())
            .then((res) => (rfp_application_list = res.results));
    });
</script>

<div class="bg-white p-8 rounded-lg w-[600px]">
    <div class="flex items-center">
        <p class="font-bold">Upload New Q&A Pairs</p>
        <button
            class="ml-auto rounded-full hover:bg-stone-100 transition duration-300 p-1 focus:ring-0 focus:outline-0"
            on:click={() => modalStore.clear()}
        >
            <img src={BlackX} alt="Exit" />
        </button>
    </div>

    {#if mode == "application"}
        <p class="font-semibold text-sm">Select Application</p>

        <div class="h-[500px] overflow-y-scroll">
            <div class="grid grid-cols-1 w-full gap-y-2 mt-3">
                {#if rfp_application_resp}
                    {#await rfp_application_resp}
                        <p class="py-10 text-center w-full">Loading...</p>
                    {:then}
                        {#each rfp_application_list as application}
                            <button
                                class="w-full rounded-lg border border-stone-200 py-2 px-4 bg-stone-100 hover:bg-stone-200 transition duration-300 flex flex-col focus:outline-0"
                                on:click={() => choose_application(application.application_id)}
                            >
                                <p class="text-sm font-semibold">{application.title}</p>

                                <div class="flex gap-x-3 mt-2">
                                    <div class="px-3 py-1 rounded-full border border-black text-xs">
                                        Created at {format_date(application.created_at)}
                                    </div>
                                    <div class="px-3 py-1 rounded-full border border-black text-xs">
                                        {#if application.due_date}
                                            Due {format_date(application.due_date)}
                                        {:else}
                                            No Due Date
                                        {/if}
                                    </div>
                                    <div
                                        class="{status_colors[
                                            application.status ?? 'Cancelled'
                                        ]} text-white text-xs mr-3 px-3 py-1 rounded-full w-fit cursor-pointer transition duration-300"
                                    >
                                        {application.status}
                                    </div>
                                </div>
                            </button>
                        {/each}
                    {/await}
                {/if}
            </div>
        </div>

        <button class="flex mt-1" on:click={() => (mode = "manual")}>
            <p class="text-xs underline text-stone-600 hover:text-black transition duration-300">Upload Manually</p>
        </button>
    {:else if mode == "question"}
        <p class="font-semibold text-sm">Choose Questions</p>

        <div class="flex rounded-full bg-white border border-zinc-400 h-10 flex items-center p-2 w-fit">
            <input
                type="text"
                class="text-sm h-6 border-transparent focus:border-transparent focus:ring-0 focus:outline-0"
                placeholder="Search"
                bind:value={question_search_input}
            />

            <div class="size-6 p-1">
                <img src={SearchGlass} alt="Search" />
            </div>
        </div>

        <div class="h-[500px] overflow-y-scroll">
            <div class="grid grid-cols-1 w-full gap-y-2 mt-3">
                {#if questions_resp}
                    {#await questions_resp}
                        <p class="py-10 text-center w-full">Loading...</p>
                    {:then}
                        {#if filter_questions(questions_list, question_search_input).length}
                            {#each filter_questions(questions_list, question_search_input) as question}
                                {#if question.name && question.response_text}
                                    <button
                                        class="w-full rounded-lg border border-stone-200 py-2 px-4 transition duration-300 flex flex-col focus:outline-0"
                                        class:bg-stone-100={!selected_questions.has(question.id ?? "")}
                                        class:bg-stone-300={selected_questions.has(question.id ?? "")}
                                        class:hover:bg-stone-200={!selected_questions.has(question.id ?? "")}
                                        on:click={() => toggle_question_select(question.id)}
                                    >
                                        <p class="font-semibold text-sm text-left line-clamp-2">{question.name}</p>
                                        <p class="text-sm text-left mt-2 line-clamp-5">{question.response_text}</p>
                                    </button>
                                {/if}
                            {/each}
                        {:else}
                            <p class="py-10 text-center text-sm">No Questions Found</p>
                        {/if}
                    {/await}
                {/if}
            </div>
        </div>

        <div class="flex gap-x-3 mt-5">
            <button
                class="px-4 py-2 bg-zinc-800 rounded-full hover:shadow-md transition duration-300 text-white text-sm disabled:bg-stone-200"
                disabled={!selected_questions.size || submitting}
                on:click={submit_questions}
            >
                Upload {selected_questions.size} Selected
            </button>

            <button
                class="px-4 py-2 bg-zinc-800 rounded-full hover:shadow-md transition duration-300 text-sm text-white"
                on:click={() => (mode = "application")}
            >
                Select Another Application
            </button>
        </div>

        <p class="text-stone-600 text-xs mt-3">
            Questions whose question text or response have not been filled out are omitted.
        </p>

        <button class="flex mt-1" on:click={() => (mode = "manual")}>
            <p class="text-xs underline text-stone-600 hover:text-black transition duration-300">
                Upload Manually Instead
            </p>
        </button>
    {:else if mode == "manual"}
        <p class="font-semibold text-sm">Manually Upload Q&A Pair</p>

        <p class="font-semibold text-sm mt-3">Question</p>
        <textarea
            class="w-full text-xs focus:outline-0 rounded-lg h-[230px] border border-stone-300"
            bind:value={manual_question_name}
        />

        <p class="font-semibold text-sm">Answer</p>
        <textarea
            class="w-full text-xs focus:outline-0 rounded-lg h-[230px] border border-stone-300"
            bind:value={manual_question_response}
        />

        <button
            class="px-4 py-2 bg-zinc-800 disabled:bg-stone-200 text-white rounded-full text-sm transition duration-300"
            disabled={!manual_question_name || !manual_question_response}
            use:popup={manual_submit_popup}
            on:click={submit_manual}
        >
            Submit
        </button>

        <div
            data-popup="manual_submit_popup"
            class="bg-zinc-800 text-white rounded-lg hover:shadow-md transition duration-300"
        >
            <!-- Written like this so the popup can be disabled when it's not relevant -->
            <div class="mx-4 my-2" class:hidden={manual_question_name && manual_question_response}>
                <p class="text-sm">You must fill out both fields before submitting.</p>
                <div class="arrow bg-zinc-800" />
            </div>
        </div>

        <button class="flex mt-1" on:click={() => (mode = "application")}>
            <p class="text-xs underline text-stone-600 hover:text-black transition duration-300">
                Select pre-existing questions instead
            </p>
        </button>
    {/if}
</div>
